import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CloudUpload, Loader2, Scissors } from "lucide-react";

export const NewProjectDialog = ({ open, onOpenChange, onCreate }) => {
    const [name, setName] = useState("");
    const [streetAddress, setStreetAddress] = useState("");
    const [city, setCity] = useState("");
    const [province, setProvince] = useState("");
    const [country, setCountry] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [jobId, setJobId] = useState("");
    const [autoSplitPdfs, setAutoSplitPdfs] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setName("");
        setStreetAddress("");
        setCity("");
        setProvince("");
        setCountry("");
        setPostalCode("");
        setJobId("");
        setAutoSplitPdfs(true);
    };

    // iter-169 — legacy `location` string is auto-composed from the
    // structured fields so backend callers and older UI panels that
    // still read `project.location` keep working.
    const composedLocation = (() => {
        const s = streetAddress.trim();
        const c = city.trim();
        const pv = province.trim();
        const cn = country.trim();
        const p = postalCode.trim();
        const cityLine = [c, pv, cn].filter(Boolean).join(", ");
        return [s, cityLine, p].filter(Boolean).join(", ");
    })();

    const isValid =
        name.trim().length > 0 &&
        streetAddress.trim().length > 0 &&
        city.trim().length > 0 &&
        jobId.trim().length > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid) return;
        setSubmitting(true);
        try {
            await onCreate({
                name: name.trim(),
                location: composedLocation,
                street_address: streetAddress.trim(),
                city: city.trim(),
                province: province.trim(),
                country: country.trim(),
                postal_code: postalCode.trim(),
                job_id: jobId.trim(),
                auto_split_pdfs: autoSplitPdfs,
            });
            reset();
            onOpenChange(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#141414] border border-white/15 text-white w-[calc(100vw-1.5rem)] max-w-[460px] overflow-hidden p-4 sm:p-6"
                data-testid="new-project-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white">Start a new project</DialogTitle>
                    <DialogDescription className="text-slate-400">
                        Name it, drop an address and project ID, then upload plans.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="project-name" className="text-slate-300">
                            Customer name
                        </Label>
                        <Input
                            id="project-name"
                            data-testid="input-project-name"
                            placeholder="e.g. Baker Warehouse"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="project-street" className="text-slate-300">
                            Street address
                        </Label>
                        <Input
                            id="project-street"
                            data-testid="input-project-street"
                            placeholder="e.g. 4016 Carlyon Line"
                            value={streetAddress}
                            onChange={(e) => setStreetAddress(e.target.value)}
                            className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                            <Label htmlFor="project-city" className="text-slate-300">
                                City
                            </Label>
                            <Input
                                id="project-city"
                                data-testid="input-project-city"
                                placeholder="e.g. Severn"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="project-province" className="text-slate-300">
                                Province / State
                            </Label>
                            <Input
                                id="project-province"
                                data-testid="input-project-province"
                                placeholder="e.g. ON"
                                value={province}
                                onChange={(e) => setProvince(e.target.value)}
                                className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                            <Label htmlFor="project-country" className="text-slate-300">
                                Country
                            </Label>
                            <Input
                                id="project-country"
                                data-testid="input-project-country"
                                placeholder="e.g. Canada"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="project-postal" className="text-slate-300">
                                Postal code
                            </Label>
                            <Input
                                id="project-postal"
                                data-testid="input-project-postal"
                                placeholder="e.g. L3V 0W1"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="project-job-id" className="text-slate-300">
                            Project ID
                        </Label>
                        <Input
                            id="project-job-id"
                            data-testid="input-project-job-id"
                            placeholder="e.g. PRJ-2026-0481"
                            value={jobId}
                            onChange={(e) => setJobId(e.target.value)}
                            className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                            required
                        />
                    </div>
                    <div className="flex items-start gap-3 rounded-sm border border-white/15 bg-white/[0.05] p-3">
                        <div className="shrink-0 mt-0.5">
                            <Scissors className="w-4 h-4 text-[#8fa4f0]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-3">
                                <Label
                                    htmlFor="project-auto-split"
                                    className="text-slate-200 font-semibold text-sm cursor-pointer"
                                >
                                    Auto-split multi-page PDFs
                                </Label>
                                <Switch
                                    id="project-auto-split"
                                    checked={autoSplitPdfs}
                                    onCheckedChange={setAutoSplitPdfs}
                                    data-testid="toggle-auto-split-pdfs"
                                    className="data-[state=checked]:bg-[#1a3fd1]"
                                />
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                                {autoSplitPdfs
                                    ? "Every page of a multi-page PDF upload becomes its own pinnable row."
                                    : "Multi-page PDFs stay as a single row. You can split later from the sidebar."}
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            data-testid="btn-cancel-new-project"
                            className="text-slate-300 hover:text-white hover:bg-white/10"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!isValid || submitting}
                            data-testid="btn-submit-new-project"
                            className="bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white font-bold disabled:opacity-40 disabled:bg-[#1a1a1a] disabled:text-slate-400 disabled:cursor-not-allowed disabled:hover:bg-white/10"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <CloudUpload className="w-4 h-4 mr-2" />
                                    Upload Plans
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default NewProjectDialog;
